import React, { useState } from 'react';
import { X, FileDown, Loader2 } from 'lucide-react';
import { subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { Sale, Customer, Payment } from '../../types';
import { reportService } from '../../services/firebase';
import { loadLogoAsBase64, getContactInfo } from '../../utils/receiptUtils';
import { generateReportPDF, ReportPDFConfig, ReportPDFData } from '../../utils/reportPdfGenerator';

interface ExportReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentDateRange: string;
  orders: Sale[];
  payments: Payment[];
  customers: Customer[];
}

type DatePreset = '7' | '30' | '90' | '180' | 'week' | 'month' | 'custom';

const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'This month' },
  { value: '90', label: 'Last 3 months' },
  { value: '180', label: 'Last 6 months' },
  { value: 'custom', label: 'Custom date range' },
];

const ExportReportModal: React.FC<ExportReportModalProps> = ({
  isOpen,
  onClose,
  currentDateRange,
  orders,
  payments,
  customers,
}) => {
  const mapCurrentRange = (): DatePreset => {
    if (['7', '30', '90', '180'].includes(currentDateRange)) return currentDateRange as DatePreset;
    if (currentDateRange === '365') return '180';
    return '30';
  };

  const [preset, setPreset] = useState<DatePreset>(mapCurrentRange());
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [sections, setSections] = useState({
    keyMetrics: true,
    ordersTable: true,
    paymentSummary: true,
    topCustomers: true,
    statusDistribution: true,
  });

  const toggleSection = (key: keyof typeof sections) => {
    setSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const resolveDateRange = (): { startDate: Date; endDate: Date } | null => {
    const now = new Date();
    switch (preset) {
      case '7':
        return { startDate: subDays(now, 7), endDate: now };
      case '30':
        return { startDate: subDays(now, 30), endDate: now };
      case '90':
        return { startDate: subDays(now, 90), endDate: now };
      case '180':
        return { startDate: subDays(now, 180), endDate: now };
      case 'week':
        return { startDate: startOfWeek(now, { weekStartsOn: 1 }), endDate: endOfWeek(now, { weekStartsOn: 1 }) };
      case 'month':
        return { startDate: startOfMonth(now), endDate: endOfMonth(now) };
      case 'custom': {
        if (!customStart || !customEnd) {
          setError('Please select both start and end dates.');
          return null;
        }
        const start = new Date(customStart);
        const end = new Date(customEnd);
        if (start > end) {
          setError('Start date must be before end date.');
          return null;
        }
        // Set end to end of day
        end.setHours(23, 59, 59, 999);
        return { startDate: start, endDate: end };
      }
      default:
        return { startDate: subDays(now, 30), endDate: now };
    }
  };

  const handleExport = async () => {
    setError('');
    const range = resolveDateRange();
    if (!range) return;

    setExporting(true);
    try {
      // Determine whether we can reuse current data or need fresh fetch
      let exportOrders = orders;
      let exportPayments = payments;

      // If the preset differs from what's currently loaded, fetch fresh data
      const needsFresh = preset !== currentDateRange;
      if (needsFresh) {
        const [freshOrders, freshPayments] = await Promise.all([
          reportService.getOrdersByDateRange(range.startDate, range.endDate),
          reportService.getPaymentsByDateRange(range.startDate, range.endDate),
        ]);
        exportOrders = freshOrders;
        exportPayments = freshPayments;
      }

      // Load logo and contact info
      const [logoBase64, contactInfo] = await Promise.all([
        loadLogoAsBase64(),
        Promise.resolve(getContactInfo()),
      ]);

      const data: ReportPDFData = {
        orders: exportOrders,
        payments: exportPayments,
        customers,
        contactInfo,
        logoBase64,
      };

      const config: ReportPDFConfig = {
        startDate: range.startDate,
        endDate: range.endDate,
        sections,
      };

      await generateReportPDF(data, config);
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (!isOpen) return null;

  const sectionOptions = [
    { key: 'keyMetrics' as const, label: 'Key Metrics Summary' },
    { key: 'ordersTable' as const, label: 'Orders Breakdown Table' },
    { key: 'paymentSummary' as const, label: 'Payment Summary & Details' },
    { key: 'topCustomers' as const, label: 'Top Customers' },
    { key: 'statusDistribution' as const, label: 'Order Status Distribution' },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-80 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6 z-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <FileDown className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Export Report as PDF</h2>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Date Range */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date Range</label>
            <select
              value={preset}
              onChange={(e) => { setPreset(e.target.value as DatePreset); setError(''); }}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            >
              {DATE_PRESETS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>

          {/* Custom date inputs */}
          {preset === 'custom' && (
            <div className="mb-5 grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">From</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => { setCustomStart(e.target.value); setError(''); }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">To</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => { setCustomEnd(e.target.value); setError(''); }}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          )}

          {/* Section toggles */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sections to Include</label>
            <div className="space-y-2">
              {sectionOptions.map(opt => (
                <label key={opt.key} className="flex items-center gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={sections[opt.key]}
                    onChange={() => toggleSection(opt.key)}
                    className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white">
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={exporting}
              className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-60 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {exporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileDown className="h-4 w-4" />
                  Export PDF
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportReportModal;
